import { Link, useRouterState } from '@tanstack/react-router';
import {
  CalendarDays,
  Inbox,
  Layers,
  ListChecks,
  Search,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUnreadCount } from '@/data/queries';
import { useSession } from '@/state/session';
import { usePrefs, useUI } from '@/state/ui';

interface RailItem {
  to?: string;
  label: string;
  icon: LucideIcon;
  match: (p: string) => boolean;
  onClick?: () => void;
  badge?: number;
}

/** The dark icon rail on the far left (desktop). */
export function Rail() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const unread = useUnreadCount();
  const setPalette = useUI((s) => s.setPaletteOpen);
  const collapsed = usePrefs((s) => s.sidebarCollapsed);
  const setCollapsed = usePrefs((s) => s.setSidebarCollapsed);
  const email = useSession((s) => s.email);
  const items: RailItem[] = [
    {
      to: '/',
      label: 'My Tasks',
      icon: ListChecks,
      match: (p) => p === '/' || p.startsWith('/t/'),
    },
    {
      to: '/inbox',
      label: 'Inbox',
      icon: Inbox,
      match: (p) => p.startsWith('/inbox'),
      badge: unread,
    },
    {
      label: 'Spaces',
      icon: Layers,
      match: (p) => p.startsWith('/l/') || p.startsWith('/spaces'),
      onClick: () => setCollapsed(!collapsed),
    },
    {
      to: '/calendar',
      label: 'Calendar',
      icon: CalendarDays,
      match: (p) => p.startsWith('/calendar'),
    },
    { label: 'Search', icon: Search, match: () => false, onClick: () => setPalette(true) },
  ];
  const cls = (on: boolean) =>
    cn(
      'relative flex w-12 flex-col items-center gap-[3px] rounded-sm pt-1.5 pb-[5px] text-[10px] leading-3 transition-colors',
      on ? 'bg-rail-active text-white' : 'text-rail-ink hover:bg-white/10 hover:text-white',
    );
  return (
    <nav className="flex w-14 shrink-0 flex-col items-center gap-1 bg-rail py-2" aria-label="Main">
      <Link to="/" aria-label="Karya home" className="mb-2">
        <img src="/icons/icon.svg" alt="" width={32} height={32} className="rounded-[8px]" />
      </Link>
      {items.map((it) => {
        const Icon = it.icon;
        const inner = (
          <>
            <Icon className="size-[18px]" />
            <span>{it.label}</span>
            {!!it.badge && (
              <span className="absolute top-0.5 right-2 rounded-full bg-overdue px-1 text-[10px] leading-[14px] font-semibold text-white">
                {it.badge > 99 ? '99+' : it.badge}
              </span>
            )}
          </>
        );
        return it.to ? (
          <Link
            key={it.label}
            to={it.to}
            className={cls(it.match(path))}
            aria-current={it.match(path) ? 'page' : undefined}
          >
            {inner}
          </Link>
        ) : (
          <button key={it.label} onClick={it.onClick} className={cls(it.match(path))}>
            {inner}
          </button>
        );
      })}
      <span className="flex-1" />
      <Link
        to="/settings"
        className={cls(path.startsWith('/settings'))}
        aria-current={path.startsWith('/settings') ? 'page' : undefined}
      >
        <Settings className="size-[18px]" />
        <span>Settings</span>
      </Link>
      <span
        className="mt-1.5 flex size-7 items-center justify-center rounded-full bg-primary text-cap font-semibold text-on-primary"
        title={email ?? 'Demo'}
      >
        {(email ?? 'D').slice(0, 2).toUpperCase()}
      </span>
    </nav>
  );
}
